import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      profile: null,
      accessToken: null,
      refreshToken: null,
      guestToken: null, // Guest token for anonymous visitors
      isAuthenticated: false,
      isLoading: false,
      error: null,
      profilePictureVersion: 0,
      apparentRoleId: null, // For admin role testing
      authInitialized: false, // Track if auth initialization is complete

      // Actions
      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      clearError: () => set({ error: null }),

      // Fetch guest token for anonymous visitors
      fetchGuestToken: async () => {
        try {
          const response = await fetch('/api/auth/guest-token');
          const data = await response.json();

          if (response.ok && data.success) {
            set({ guestToken: data.accessToken });
            return data.accessToken;
          }
          return null;
        } catch (error) {
          console.error('Failed to fetch guest token:', error);
          return null;
        }
      },

      // Initialize auth - get guest token if not authenticated
      initializeAuth: async () => {
        const { isAuthenticated, fetchGuestToken } = get();

        // If not authenticated, always fetch a fresh guest token
        if (!isAuthenticated) {
          await fetchGuestToken();
        }

        // Mark auth as initialized
        set({ authInitialized: true });
      },

      // Get the current token (user token or guest token)
      getCurrentToken: () => {
        const { accessToken, guestToken } = get();
        return accessToken || guestToken;
      },

      login: async (email, password) => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch('/api/auth/login', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Login failed');
          }

          set({
            user: data.user,
            profile: data.profile,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          return { success: true };
        } catch (error) {
          set({
            isLoading: false,
            error: error.message,
          });
          return { success: false, error: error.message };
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch('/api/auth/register', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Registration failed');
          }

          set({
            user: data.user,
            profile: data.profile,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          return { success: true };
        } catch (error) {
          set({
            isLoading: false,
            error: error.message,
          });
          return { success: false, error: error.message };
        }
      },

      logout: async () => {
        const { accessToken } = get();

        try {
          await fetch('/api/auth/logout', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          });
        } catch (error) {
          console.error('Logout error:', error);
        }

        set({
          user: null,
          profile: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          apparentRoleId: null,
        });

        // Fetch new guest token after logout
        await get().fetchGuestToken();
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();

        if (!refreshToken) {
          return false;
        }

        try {
          const response = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error('Token refresh failed');
          }

          set({
            accessToken: data.accessToken,
          });

          return true;
        } catch (error) {
          // If refresh fails, logout
          get().logout();
          return false;
        }
      },

      updateProfile: (profile) => set({ profile }),

      refreshProfilePicture: () => set((state) => ({ profilePictureVersion: state.profilePictureVersion + 1 })),

      setApparentRoleId: (roleId) => set({ apparentRoleId: roleId }),
    }),
    {
      name: 'kwasai-auth',
      partialize: (state) => ({
        user: state.user,
        profile: state.profile,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        guestToken: state.guestToken,
        isAuthenticated: state.isAuthenticated,
        apparentRoleId: state.apparentRoleId,
      }),
    }
  )
);

export { useAuthStore };
